import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/config";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Trash2, PackagePlus, Leaf, ArrowLeft, CheckCircle2,
         Sparkles, ImagePlus, X, Plus } from "lucide-react";
import { toast } from "sonner";
import AdvancedAddonForm, { ADVANCED_ADDON_KEYS } from "./AdvancedAddonForm";
import AreaSelector from "./AreaSelector";

/**
 * iter124 — Extra Work now uses the **exact same wizard** as Add Service.
 *
 * Customer picks a service → answers only the pricing questions → the shared
 * `/api/addons/price`, `/api/estimates/calculate`, or `/api/quotes/range`
 * engines compute the price instantly. Customer can add multiple services to
 * a cart, sees a running subtotal, and submits the whole request in one shot.
 *
 * No manual price/quantity fields anywhere — the customer never types a price.
 *
 * Props:
 *   open, onOpenChange
 *   booking      — the booking object being modified
 *   mode: "admin" | "customer"  (default: "customer")
 *   onSaved(data) — fires after successful save with { checkout_url, delta, ... }
 */

// Same range-service presets used in AddServiceDialog (single source of truth).
const RANGE_KEYS = new Set(["landscaping"]); // hedge_trimming/yard_cleanup/mulching now advanced
const RANGE_PRESETS = {
  landscaping: { project_size: "small", label: "Small project" },
};

// Services that only get a range quote (no pricing engine — admin quotes on-site).
// These are the `quote_only=true` catalog entries. The customer can still
// attach photos + notes; price submits as 0 with needs_quote=true.
const QUOTE_ONLY_HELP =
  "We’ll confirm exact pricing after we see it. Add up to 5 photos and any details below.";

const MAX_QUOTE_PHOTOS = 5;
const QUICK_SQFT_BUTTONS = [1000, 2500, 5000];

// iter143 — Custom Extra Work presets (admin/crew-only). Admin picks a title
// from this list (or types their own) and enters any price / photos / notes.
const CUSTOM_TITLE_PRESETS = [
  "Haul Away Debris",
  "Fence Repair",
  "Move Rocks",
  "Install Mailbox",
  "Extra Bush Cleanup",
  "Pressure Wash Shed",
  "Hand Pull Weeds",
  "Other",
];

const EXTRA_FREQ_OPTIONS = [
  { key: "one_time", label: "One-time" },
  { key: "weekly", label: "Weekly" },
  { key: "biweekly", label: "Every 2 weeks" },
  { key: "monthly", label: "Monthly" },
];

function ExtraWorkFrequencyPicker({ value, onChange, recurringAllowed }) {
  return (
    <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-3" data-testid="extra-work-frequency">
      <Label htmlFor="ew-frequency" className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-emerald-900">
        How often?
      </Label>
      <select id="ew-frequency" value={value} onChange={(e) => onChange(e.target.value)}
              className="mt-1.5 w-full h-10 px-3 rounded-lg border-2 border-emerald-200 bg-white text-sm font-bold text-gray-900 focus:outline-none focus:border-emerald-500"
              data-testid="extra-work-frequency-select">
        {EXTRA_FREQ_OPTIONS.map((o) => (
          <option key={o.key} value={o.key} disabled={o.key !== "one_time" && !recurringAllowed}>
            {o.label}
          </option>
        ))}
      </select>
      <p className="mt-1.5 text-[11px] text-gray-600" data-testid="extra-work-frequency-hint">
        {!recurringAllowed
          ? "This visit isn't part of a recurring service, so extra work is one-time only."
          : value === "one_time"
            ? "Charged once, on this visit only."
            : "Added to this property's future visits on this schedule — charged once per visit, separate from mowing."}
      </p>
    </div>
  );
}

function _defaultSqftFor(booking) {
  const s = Number(booking?.square_feet);
  return Number.isFinite(s) && s > 0 ? s : 2000;
}

export default function AddExtraWorkDialog({ open, onOpenChange, booking, mode = "customer", onSaved }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Cart of configured items (each already has a computed price from the wizard).
  const [cart, setCart] = useState([]);
  const [notes, setNotes] = useState("");
  // iter260 — Admin-only Extra Work frequency (one-time or recurring).
  const [frequency, setFrequency] = useState("one_time");
  const bookingIsRecurring = !!booking?.frequency && !["one_time", "every_visit"].includes(booking.frequency);

  // Wizard state (mirrors AddServiceDialog: pick → config)
  const [step, setStep] = useState("pick");        // "pick" | "config"
  const [selectedKey, setSelectedKey] = useState(null);
  const [sqft, setSqft] = useState(_defaultSqftFor(booking));
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quote, setQuote] = useState(null);        // basic + range quote object
  const [advancedQuote, setAdvancedQuote] = useState(null); // advanced wizard quote
  // iter126 — shared area selection (basic sqft + quote-only paths).
  const [areas, setAreas] = useState([]);
  const [quoteOnlyPhotos, setQuoteOnlyPhotos] = useState([]);
  const [quoteOnlyNotes, setQuoteOnlyNotes] = useState("");
  const [quoteOnlyUploading, setQuoteOnlyUploading] = useState(false);
  const quoteOnlyFileRef = useRef(null);

  // iter143 — Custom Extra Work state (admin-only flow). One-off charge with
  // free-text title, description, any price, before/after photos, notes.
  const [customTitle, setCustomTitle] = useState("");
  const [customDescription, setCustomDescription] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [customQuantity, setCustomQuantity] = useState(1);
  const [customBeforePhotos, setCustomBeforePhotos] = useState([]);
  const [customAfterPhotos, setCustomAfterPhotos] = useState([]);
  const [customInternalNotes, setCustomInternalNotes] = useState("");
  const [customPhotoUploading, setCustomPhotoUploading] = useState(null); // "before" | "after" | null
  const customBeforeFileRef = useRef(null);
  const customAfterFileRef = useRef(null);

  // Load full services catalog (include quote-only so customer sees ALL 26).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get("/services?include_quote_only=true").then(({ data }) => {
      if (cancelled) return;
      setServices(data?.services || []);
    }).catch(() => setServices([])).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open]);

  // Full reset when dialog closes
  useEffect(() => {
    if (!open) {
      setCart([]); setNotes(""); setFrequency("one_time"); setStep("pick"); setSelectedKey(null);
      setSqft(_defaultSqftFor(booking));
      setQuote(null); setAdvancedQuote(null); setLoadingQuote(false);
      setAreas([]);  // iter126 — reset area selection between dialog opens
      setQuoteOnlyPhotos([]); setQuoteOnlyNotes("");
      // iter143 — reset Custom Extra Work state
      setCustomTitle(""); setCustomDescription(""); setCustomPrice("");
      setCustomQuantity(1); setCustomBeforePhotos([]); setCustomAfterPhotos([]);
      setCustomInternalNotes(""); setCustomPhotoUploading(null);
    }
  }, [open, booking]);

  const selected = useMemo(
    () => services.find((s) => s.key === selectedKey),
    [services, selectedKey]
  );
  const isAdvanced = selectedKey ? ADVANCED_ADDON_KEYS.has(selectedKey) : false;
  const isRange = selectedKey && !isAdvanced ? RANGE_KEYS.has(selectedKey) : false;
  const isQuoteOnly = !!selected?.quote_only && !isAdvanced && !isRange;

  // Basic sqft services need /estimates/calculate; range needs /quotes/range;
  // advanced handled by AdvancedAddonForm; quote-only skips pricing entirely.
  useEffect(() => {
    if (step !== "config" || !selectedKey || isAdvanced || isQuoteOnly) return;
    // iter126 — require at least one selected area before pricing.
    if (!isRange && !areas.length) { setQuote(null); return; }
    let active = true;
    setLoadingQuote(true);
    (async () => {
      try {
        if (isRange) {
          const preset = RANGE_PRESETS[selectedKey] || {};
          const { data } = await api.post("/quotes/range", { service_key: selectedKey, ...preset });
          if (active) setQuote({
            price: data.midpoint || ((data.low + data.high) / 2),
            label: preset.label || selected?.name || "",
            low: data.low, high: data.high, is_range: true,
          });
        } else {
          const { data } = await api.post("/estimates/calculate", {
            service_key: selectedKey,
            square_feet: sqft,
            frequency: "one_time",
            yard_area: "both",
            areas,  // iter126 — multi-area drives labor multiplier
            area_already_scaled: false,
          });
          if (active) setQuote({
            price: data.total,
            label: (data.areas_label || `${sqft.toLocaleString()} sqft`),
            is_range: false,
            areas_label: data.areas_label,
          });
        }
      } catch {
        if (active) setQuote(null);
      } finally {
        if (active) setLoadingQuote(false);
      }
    })();
    return () => { active = false; };
  }, [step, selectedKey, sqft, isRange, isAdvanced, isQuoteOnly, selected, areas]);

  const handleSelect = (key) => {
    setSelectedKey(key);
    setQuote(null); setAdvancedQuote(null);
    setAreas([]);  // iter126 — reset per selection so customer explicitly picks
    setQuoteOnlyPhotos([]); setQuoteOnlyNotes("");
    setStep("config");
  };

  const handleBack = () => {
    setStep("pick");
    setSelectedKey(null);
    setQuote(null); setAdvancedQuote(null);
    setAreas([]);  // iter126 — clear on step-back
    setQuoteOnlyPhotos([]); setQuoteOnlyNotes("");
    // iter143 — clear custom form on step-back so re-open is a fresh slate.
    setCustomTitle(""); setCustomDescription(""); setCustomPrice("");
    setCustomQuantity(1); setCustomBeforePhotos([]); setCustomAfterPhotos([]);
    setCustomInternalNotes("");
  };

  // iter143 — Enter the custom-work form. Only admin/crew see the entry card.
  const handleCustom = () => {
    setSelectedKey(null);
    setQuote(null); setAdvancedQuote(null);
    setStep("custom");
  };

  // iter143 — Shared photo uploader for custom Before/After. Same
  // `/quotes/upload/photo` endpoint the quote-only path uses.
  const uploadCustomPhotos = async (files, role /* "before" | "after" */) => {
    if (!files?.length) return;
    const setPhotos = role === "before" ? setCustomBeforePhotos : setCustomAfterPhotos;
    const currentList = role === "before" ? customBeforePhotos : customAfterPhotos;
    const remaining = MAX_QUOTE_PHOTOS - currentList.length;
    if (remaining <= 0) { toast.error(`Up to ${MAX_QUOTE_PHOTOS} ${role} photos`); return; }
    setCustomPhotoUploading(role);
    try {
      const uploaded = [];
      for (const f of Array.from(files).slice(0, remaining)) {
        if (!/^image\/(jpe?g|png|heic|heif)$/i.test(f.type)) {
          toast.error(`Skipped ${f.name}: only JPG / PNG / HEIC`); continue;
        }
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`Skipped ${f.name}: over 10 MB`); continue;
        }
        const data_url = await new Promise((resolve, reject) => {
          const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject;
          r.readAsDataURL(f);
        });
        const { data } = await api.post("/quotes/upload/photo", { data_url });
        uploaded.push({ media_id: data.media_id, data_url });
      }
      if (uploaded.length) {
        setPhotos((prev) => [...prev, ...uploaded]);
        toast.success(`${uploaded.length} ${role} photo${uploaded.length === 1 ? "" : "s"} attached`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setCustomPhotoUploading(null);
    }
  };

  // Quote-only photo picker
  const pickQuoteOnlyPhoto = () => quoteOnlyFileRef.current?.click();
  const onQuoteOnlyFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = MAX_QUOTE_PHOTOS - quoteOnlyPhotos.length;
    if (remaining <= 0) { toast.error(`Up to ${MAX_QUOTE_PHOTOS} photos`); return; }
    setQuoteOnlyUploading(true);
    try {
      const uploaded = [];
      for (const f of files.slice(0, remaining)) {
        if (!/^image\/(jpe?g|png|heic|heif)$/i.test(f.type)) {
          toast.error(`Skipped ${f.name}: only JPG / PNG / HEIC`); continue;
        }
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`Skipped ${f.name}: over 10 MB`); continue;
        }
        const data_url = await new Promise((resolve, reject) => {
          const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject;
          r.readAsDataURL(f);
        });
        const { data } = await api.post("/quotes/upload/photo", { data_url });
        uploaded.push({ media_id: data.media_id, data_url });
      }
      if (uploaded.length) {
        setQuoteOnlyPhotos((prev) => [...prev, ...uploaded]);
        toast.success(`${uploaded.length} photo${uploaded.length === 1 ? "" : "s"} attached`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setQuoteOnlyUploading(false);
    }
  };

  // "Add to request" — pushes the configured service into the cart.
  const addToCart = () => {
    if (!selectedKey || !selected) return;
    if (isAdvanced) {
      if (!advancedQuote) return;
      setCart((prev) => [...prev, {
        service_key: selectedKey,
        service_name: advancedQuote.service_name || selected.name,
        price: Number(advancedQuote.price.toFixed(2)),
        quantity: 1,
        label: advancedQuote.summary_label || "",
        line_items: advancedQuote.line_items || [],
        options: advancedQuote.options || {},
        photo_media_ids: advancedQuote.photo_media_ids || [],
        is_advanced: true,
        // iter126 — canonical areas came from the wizard's step-0 map.
        areas: advancedQuote.options?.areas || [],
      }]);
    } else if (isQuoteOnly) {
      // Quote-only: no engine — submit with price=0 and needs_quote=true.
      setCart((prev) => [...prev, {
        service_key: selectedKey,
        service_name: selected.name,
        price: 0,
        quantity: 1,
        label: "Needs on-site quote",
        is_quote_only: true,
        photo_media_ids: quoteOnlyPhotos.map((p) => p.media_id),
        notes: (quoteOnlyNotes || "").trim(),
        areas,  // iter126 — location saved for the crew work order
      }]);
    } else {
      if (!quote) return;
      setCart((prev) => [...prev, {
        service_key: selectedKey,
        service_name: selected.name,
        price: Number(quote.price.toFixed(2)),
        quantity: 1,
        label: quote.label || "",
        is_range: !!quote.is_range,
        areas,  // iter126 — canonical area list
        ...(isRange ? {} : { square_feet: sqft }),
      }]);
    }
    // Snap back to pick screen so the customer can add another service.
    handleBack();
  };

  // iter143 — Push the custom item into the cart. All server-side validation
  // (admin-only, price ≤ $10k, non-empty title) also runs on submit.
  const addCustomToCart = () => {
    const title = customTitle.trim();
    if (!title) { toast.error("Title is required"); return; }
    const price = parseFloat(customPrice);
    if (!Number.isFinite(price) || price <= 0) { toast.error("Enter a valid price"); return; }
    if (price > 10_000) { toast.error("Price cannot exceed $10,000"); return; }
    const qty = Math.max(1, parseInt(customQuantity || "1", 10) || 1);
    setCart((prev) => [...prev, {
      service_key: null,      // signals custom to backend (validator sets it to "custom")
      service_name: title,
      price: Number(price.toFixed(2)),
      quantity: qty,
      is_custom: true,
      description: customDescription.trim() || "",
      before_photo_media_ids: customBeforePhotos.map((p) => p.media_id),
      after_photo_media_ids: customAfterPhotos.map((p) => p.media_id),
      internal_notes: customInternalNotes.trim() || "",
      // notes = customer-facing description for parity with existing renderers.
      notes: customDescription.trim() || "",
      label: qty > 1 ? `Qty × ${qty}` : "Custom Extra Work",
    }]);
    handleBack();
  };

  const removeFromCart = (idx) =>
    setCart((prev) => prev.filter((_, i) => i !== idx));

  const subtotal = useMemo(
    () => cart.reduce((s, it) => s + Math.max(0, Number(it.price) || 0) * Math.max(1, Number(it.quantity) || 1), 0),
    [cart],
  );
  const quoteOnlyCount = cart.filter((i) => i.is_quote_only).length;
  const pricedCount = cart.length - quoteOnlyCount;

  // Add-to-cart button gating (matches AddServiceDialog exactly)
  const canAdd = isAdvanced
    ? !!advancedQuote
    : isQuoteOnly
      ? (areas.length > 0 && (quoteOnlyPhotos.length > 0 || (quoteOnlyNotes || "").trim().length > 0))
      : (areas.length > 0 && !!quote && !loadingQuote);

  const submit = async () => {
    if (!booking?.booking_id) { toast.error("No booking selected"); return; }
    if (cart.length === 0) { toast.error("Add at least one service"); return; }
    setBusy(true);
    try {
      const url = mode === "admin"
        ? `/admin/bookings/${booking.booking_id}/extra-work`
        : `/me/bookings/${booking.booking_id}/extra-work`;
      const payload = {
        items: cart.map((i) => ({
          service_key: i.service_key,
          name: i.service_name || undefined,  // iter143 — required for custom items
          price: Number(i.price) || 0,
          quantity: Math.max(1, Number(i.quantity) || 1),
          label: i.label || undefined,
          line_items: i.line_items || undefined,
          options: i.options || undefined,
          photo_media_ids: i.photo_media_ids || undefined,
          is_advanced: i.is_advanced || undefined,
          is_range: i.is_range || undefined,
          is_quote_only: i.is_quote_only || undefined,
          square_feet: i.square_feet ?? undefined,
          areas: i.areas && i.areas.length ? i.areas : undefined,  // iter126
          notes: i.notes || undefined,
          // iter143 — Custom Extra Work fields
          is_custom: i.is_custom || undefined,
          description: i.description || undefined,
          before_photo_media_ids: (i.before_photo_media_ids?.length ? i.before_photo_media_ids : undefined),
          after_photo_media_ids: (i.after_photo_media_ids?.length ? i.after_photo_media_ids : undefined),
          internal_notes: i.internal_notes || undefined,
        })),
        notes: notes.trim() || undefined,
        ...(mode === "admin" ? { frequency } : {}),
      };
      const { data } = await api.post(url, payload);
      const delta = Number(data?.delta || 0);
      toast.success(mode === "admin"
        ? (frequency !== "one_time"
            ? `Added $${delta.toFixed(2)} extra work · repeats ${EXTRA_FREQ_OPTIONS.find((o) => o.key === frequency)?.label}`
            : `Added $${delta.toFixed(2)} in extra work`)
        : (delta > 0
            ? `Extra work submitted — additional $${delta.toFixed(2)}`
            : `Extra work request submitted — we’ll quote and follow up`));
      if (data.checkout_url && mode === "customer") {
        window.location.href = data.checkout_url; return;
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit extra work");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="add-extra-work-dialog">
        <DialogHeader className="bg-gradient-to-br from-[#062818] to-[#0B4029] text-white px-5 py-4">
          <DialogTitle className="text-white font-[Outfit] tracking-tight flex items-center gap-2">
            <PackagePlus className="w-5 h-5 text-emerald-200" /> Request Extra Work
          </DialogTitle>
          <DialogDescription className="text-emerald-100/80">
            {mode === "admin"
              ? `On-site addition to booking #${booking?.booking_id?.slice(-6)}`
              : `Same wizard as Add Service — pick a service, answer the pricing questions, we lock in the price.`}
          </DialogDescription>
        </DialogHeader>

        {/* Body */}
        <div className="max-h-[75vh] overflow-y-auto">
          {/* Cart summary — always visible when there are items */}
          {cart.length > 0 && step === "pick" && (
            <div className="px-5 pt-4">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-3 space-y-2"
                   data-testid="extra-work-cart">
                <div className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-emerald-900">
                  In your request ({cart.length})
                </div>
                {cart.map((it, idx) => (
                  <div key={idx}
                       className="flex items-start gap-3 py-2 border-t border-emerald-200/60 first:border-t-0"
                       data-testid={`extra-work-cart-item-${idx}`}>
                    <span className="mt-1 w-7 h-7 rounded-lg bg-white text-emerald-700 flex items-center justify-center shrink-0">
                      <Leaf className="w-3.5 h-3.5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-extrabold text-gray-900 truncate">
                        {it.service_name}
                      </div>
                      {it.label && (
                        <div className="text-[11px] text-gray-600 truncate">{it.label}</div>
                      )}
                      {it.is_quote_only && (
                        <div className="text-[11px] font-bold text-amber-700 flex items-center gap-1 mt-0.5">
                          <Sparkles className="w-3 h-3" /> We&apos;ll quote on-site
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[13px] font-extrabold tabular-nums text-emerald-800"
                           data-testid={`extra-work-cart-item-price-${idx}`}>
                        {it.is_quote_only ? "Quote" : `$${Number(it.price).toFixed(2)}`}
                      </div>
                      <button type="button" onClick={() => removeFromCart(idx)}
                              className="mt-1 text-[11px] text-red-600 hover:underline flex items-center gap-1"
                              data-testid={`extra-work-cart-remove-${idx}`}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </div>
                ))}
                <div className="pt-2 mt-1 border-t border-emerald-200 flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-900">
                    Subtotal {quoteOnlyCount > 0 && `(${pricedCount} priced · ${quoteOnlyCount} to quote)`}
                  </span>
                  <span className="text-xl font-extrabold text-emerald-800 tabular-nums"
                        data-testid="extra-work-subtotal">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {step === "pick" ? (
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500 mb-2">
                {cart.length === 0 ? "Pick a service" : "Add another service"}
              </div>
              {loading ? (
                <div className="py-10 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-700" />
                </div>
              ) : services.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-10">No services available.</div>
              ) : (
                <ul className="grid grid-cols-2 gap-2" data-testid="extra-work-service-list">
                  {/* iter143 — Custom Extra Work card (admin/crew-only). Always
                      first so the crew's quickest option (one-off charge)
                      isn't buried below the catalog. */}
                  {mode === "admin" && (
                    <li key="__custom__" className="col-span-2">
                      <button
                        type="button"
                        onClick={handleCustom}
                        data-testid="extra-work-svc-custom"
                        className="w-full text-left rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-3 hover:border-amber-400 hover:bg-amber-50 transition active:scale-[0.98]"
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-9 h-9 rounded-xl bg-amber-500/15 text-amber-700 flex items-center justify-center shrink-0">
                            <Plus className="w-4 h-4" />
                          </span>
                          <span className="text-[13px] font-extrabold tracking-tight font-[Outfit] text-amber-900 leading-tight">
                            Custom Extra Work
                          </span>
                        </div>
                        <div className="mt-2 text-[10px] uppercase tracking-[0.14em] font-extrabold text-amber-800/80">
                          Enter any title, any price · admin / crew
                        </div>
                      </button>
                    </li>
                  )}
                  {services.map((s) => (
                    <li key={s.key}>
                      <button
                        type="button"
                        onClick={() => handleSelect(s.key)}
                        data-testid={`extra-work-svc-${s.key}`}
                        className="w-full text-left rounded-2xl border border-gray-200 bg-white p-3 hover:border-emerald-400 hover:bg-emerald-50/40 transition active:scale-[0.98]"
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-9 h-9 rounded-xl bg-emerald-50 text-[#047857] flex items-center justify-center shrink-0">
                            <Leaf className="w-4 h-4" />
                          </span>
                          <span className="text-[13px] font-extrabold tracking-tight font-[Outfit] text-gray-900 leading-tight">
                            {s.name}
                          </span>
                        </div>
                        <div className="mt-2 text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">
                          {ADVANCED_ADDON_KEYS.has(s.key) ? "Wizard · answer to price"
                            : RANGE_KEYS.has(s.key) ? "Range quote"
                            : s.quote_only ? "Photo + on-site quote"
                            : `From $${(s.min_price || 0).toFixed(0)}`}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {mode === "admin" && (
                <ExtraWorkFrequencyPicker value={frequency} onChange={setFrequency}
                                          recurringAllowed={bookingIsRecurring} />
              )}

              {/* Optional overall notes */}
              <div className="mt-4">
                <Label htmlFor="ew-notes" className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">
                  Extra notes for our team (optional)
                </Label>
                <Input id="ew-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                       placeholder="Access instructions, gate codes, anything we should know…"
                       className="mt-1.5" data-testid="extra-work-notes" />
              </div>
            </div>
          ) : step === "custom" ? (
            /* iter143 — Custom Extra Work form (admin/crew only). One-off
               charge with free-text title, description, any price, before/
               after photos, and internal notes. Server rejects `is_custom`
               submitted by non-admin users so this is safe. */
            <div className="p-5 space-y-4" data-testid="extra-work-custom-form">
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-amber-700">
                Custom Extra Work
              </div>
              <div className="text-lg font-extrabold tracking-tight font-[Outfit] text-gray-900 -mt-1">
                One-off charge · admin / crew entry
              </div>

              {/* Title (required) */}
              <div>
                <Label htmlFor="cew-title" className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">
                  Work Title <span className="text-red-500 normal-case tracking-normal">*</span>
                </Label>
                <Input id="cew-title" list="cew-title-presets"
                       value={customTitle}
                       onChange={(e) => setCustomTitle(e.target.value)}
                       placeholder="e.g. Haul Away Debris"
                       className="h-12 mt-1.5 text-[15px] font-bold"
                       data-testid="extra-work-custom-title" />
                <datalist id="cew-title-presets">
                  {CUSTOM_TITLE_PRESETS.map((t) => (<option key={t} value={t} />))}
                </datalist>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {CUSTOM_TITLE_PRESETS.slice(0, 7).map((t) => (
                    <button key={t} type="button" onClick={() => setCustomTitle(t)}
                            data-testid={`extra-work-custom-preset-${t.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-2.5 h-7 rounded-full text-[11px] font-bold border ${customTitle === t ? "border-amber-500 text-amber-700 bg-amber-50" : "border-gray-200 text-gray-600 bg-white hover:bg-amber-50/50"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description (optional, customer-facing) */}
              <div>
                <Label htmlFor="cew-desc" className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">
                  Description / Reason (optional)
                </Label>
                <textarea id="cew-desc"
                          value={customDescription}
                          onChange={(e) => setCustomDescription(e.target.value)}
                          placeholder="What was done? — the customer sees this on their invoice."
                          rows={2}
                          className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                          data-testid="extra-work-custom-description" />
              </div>

              {/* Price + Quantity */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cew-price" className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">
                    Price ($) <span className="text-red-500 normal-case tracking-normal">*</span>
                  </Label>
                  <Input id="cew-price" type="number" step="0.01" min="0.01" max="10000"
                         value={customPrice}
                         onChange={(e) => setCustomPrice(e.target.value)}
                         placeholder="0.00"
                         className="h-12 mt-1.5 text-lg font-extrabold tabular-nums"
                         data-testid="extra-work-custom-price" />
                </div>
                <div>
                  <Label htmlFor="cew-qty" className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">
                    Quantity
                  </Label>
                  <Input id="cew-qty" type="number" min="1" max="999"
                         value={customQuantity}
                         onChange={(e) => setCustomQuantity(Math.max(1, parseInt(e.target.value || "1", 10) || 1))}
                         className="h-12 mt-1.5 text-lg font-extrabold tabular-nums"
                         data-testid="extra-work-custom-quantity" />
                </div>
              </div>

              {/* Before / After photos */}
              {[
                { role: "before", label: "Before Photos", photos: customBeforePhotos, setPhotos: setCustomBeforePhotos, fileRef: customBeforeFileRef },
                { role: "after",  label: "After Photos",  photos: customAfterPhotos,  setPhotos: setCustomAfterPhotos,  fileRef: customAfterFileRef  },
              ].map(({ role, label, photos, setPhotos, fileRef }) => (
                <div key={role}>
                  <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">
                    {label} (up to {MAX_QUOTE_PHOTOS})
                  </Label>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {photos.map((p, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
                           data-testid={`extra-work-custom-${role}-photo-${i}`}>
                        <img src={p.data_url} alt="" className="w-full h-full object-cover" />
                        <button type="button"
                                onClick={() => setPhotos((prev) => prev.filter((_, x) => x !== i))}
                                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"
                                data-testid={`extra-work-custom-${role}-photo-remove-${i}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {photos.length < MAX_QUOTE_PHOTOS && (
                      <button type="button" onClick={() => fileRef.current?.click()}
                              disabled={customPhotoUploading === role}
                              className="w-20 h-20 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 text-amber-700 flex flex-col items-center justify-center gap-0.5 hover:bg-amber-50 disabled:opacity-50"
                              data-testid={`extra-work-custom-${role}-photo-add`}>
                        {customPhotoUploading === role
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <ImagePlus className="w-4 h-4" />}
                        <span className="text-[10px] font-extrabold uppercase">{role}</span>
                      </button>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" multiple
                         className="hidden"
                         onChange={(e) => {
                           const files = e.target.files; e.target.value = "";
                           uploadCustomPhotos(files, role);
                         }} />
                </div>
              ))}

              {/* Internal notes (crew-only, hidden from customer) */}
              <div>
                <Label htmlFor="cew-internal" className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">
                  Internal Notes (hidden from customer)
                </Label>
                <textarea id="cew-internal"
                          value={customInternalNotes}
                          onChange={(e) => setCustomInternalNotes(e.target.value)}
                          placeholder="Crew-only notes — time on site, materials used, follow-ups…"
                          rows={2}
                          className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                          data-testid="extra-work-custom-internal-notes" />
              </div>

              {/* Live total preview */}
              <div className="rounded-2xl bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white p-5 relative overflow-hidden">
                <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-amber-400/15 blur-2xl" />
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-extrabold">Line total</div>
                <div className="mt-1 text-4xl font-extrabold tabular-nums tracking-tighter font-[Outfit]"
                     data-testid="extra-work-custom-total">
                  ${(() => {
                    const p = parseFloat(customPrice);
                    const q = Math.max(1, parseInt(customQuantity || "1", 10) || 1);
                    const t = Number.isFinite(p) && p > 0 ? p * q : 0;
                    return t.toFixed(2);
                  })()}
                </div>
                <div className="mt-1 text-[12px] text-amber-100/80">
                  {customTitle ? customTitle : "One-off custom charge"}
                  {customQuantity > 1 ? ` · qty × ${customQuantity}` : ""}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5 space-y-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-700">
                Configure & price
              </div>
              <div className="text-lg font-extrabold tracking-tight font-[Outfit] text-gray-900 -mt-1">
                {selected?.name}
              </div>

              {isAdvanced ? (
                <AdvancedAddonForm
                  serviceKey={selectedKey}
                  frequency="one_time"
                  onQuoteChange={setAdvancedQuote}
                />
              ) : isQuoteOnly ? (
                <>
                  {/* iter127 — Yard Area picker (required even for quote-only). */}
                  <AreaSelector value={areas} onChange={setAreas} />
                  <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900"
                       data-testid="extra-work-quote-only-help">
                    <div className="flex items-center gap-2 font-extrabold">
                      <Sparkles className="w-3.5 h-3.5" /> On-site quote required
                    </div>
                    <div className="mt-1 text-amber-800/90">{QUOTE_ONLY_HELP}</div>
                  </div>
                  {/* Photo picker (1–5) */}
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">
                      Photos (1–{MAX_QUOTE_PHOTOS})
                    </Label>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {quoteOnlyPhotos.map((p, i) => (
                        <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
                             data-testid={`extra-work-qo-photo-${i}`}>
                          <img src={p.data_url} alt="" className="w-full h-full object-cover" />
                          <button type="button"
                                  onClick={() => setQuoteOnlyPhotos((prev) => prev.filter((_, x) => x !== i))}
                                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"
                                  data-testid={`extra-work-qo-photo-remove-${i}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {quoteOnlyPhotos.length < MAX_QUOTE_PHOTOS && (
                        <button type="button" onClick={pickQuoteOnlyPhoto}
                                disabled={quoteOnlyUploading}
                                className="w-20 h-20 rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50/50 text-emerald-700 flex flex-col items-center justify-center gap-0.5 hover:bg-emerald-50 disabled:opacity-50"
                                data-testid="extra-work-qo-photo-add">
                          {quoteOnlyUploading
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <ImagePlus className="w-4 h-4" />}
                          <span className="text-[10px] font-extrabold">Photo</span>
                        </button>
                      )}
                    </div>
                    <input ref={quoteOnlyFileRef} type="file" accept="image/*" multiple
                           className="hidden" onChange={onQuoteOnlyFiles} />
                  </div>
                  <div>
                    <Label htmlFor="ew-qo-notes" className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-gray-500">
                      Describe the work
                    </Label>
                    <Input id="ew-qo-notes" value={quoteOnlyNotes}
                           onChange={(e) => setQuoteOnlyNotes(e.target.value)}
                           placeholder="e.g. Remove 2 stumps near the fence"
                           className="mt-1.5"
                           data-testid="extra-work-qo-notes" />
                  </div>
                </>
              ) : (
                <>
                  {/* iter127 — Shared Yard Area picker (basic sqft path). */}
                  <AreaSelector value={areas} onChange={setAreas} />
                  {!isRange && (
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">
                        Yard / area size (sqft)
                      </Label>
                      <Input type="number" min={0} max={50000} value={sqft}
                             onChange={(e) => setSqft(Math.max(0, Math.min(50000, parseInt(e.target.value || "0", 10) || 0)))}
                             className="h-12 mt-1.5 text-lg font-extrabold tabular-nums"
                             data-testid="extra-work-sqft" />
                      <div className="mt-2 flex gap-2">
                        {QUICK_SQFT_BUTTONS.map((n) => (
                          <button key={n} type="button" onClick={() => setSqft(n)}
                                  data-testid={`extra-work-quick-${n}`}
                                  className={`px-3 h-8 rounded-full text-xs font-bold border ${sqft === n ? "border-[#047857] text-[#047857] bg-emerald-50" : "border-gray-200 text-gray-600 bg-white"}`}>
                            {n.toLocaleString()}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {isRange && (
                    <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 text-[12px] text-emerald-900">
                      <div className="flex items-center gap-2 font-extrabold">
                        <Sparkles className="w-3.5 h-3.5" /> Estimated range
                      </div>
                      <div className="mt-1 text-emerald-800/90">
                        {(RANGE_PRESETS[selectedKey]?.label) || "Standard scope"} — we&apos;ll confirm exact pricing on-site.
                      </div>
                    </div>
                  )}

                  <div className="rounded-2xl bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white p-5 relative overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-emerald-400/15 blur-2xl" />
                    <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-extrabold">Service total</div>
                    <div className="mt-1 text-4xl font-extrabold tabular-nums tracking-tighter font-[Outfit]"
                         data-testid="extra-work-price">
                      {loadingQuote ? <Loader2 className="w-7 h-7 animate-spin inline" /> : quote ? `$${quote.price.toFixed(2)}` : "—"}
                    </div>
                    {quote?.is_range && (
                      <div className="mt-1 text-[12px] text-emerald-100/80">
                        Range ${quote.low?.toFixed(0)} – ${quote.high?.toFixed(0)} · midpoint shown
                      </div>
                    )}
                    {!quote?.is_range && quote && (
                      <div className="mt-1 text-[12px] text-emerald-100/80">One-time visit · {sqft.toLocaleString()} sqft</div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === "pick" ? (
          <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between gap-2 bg-gray-50">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
                    data-testid="extra-work-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy || cart.length === 0}
                    className="bg-[#047857] hover:bg-[#065F46] text-white font-extrabold rounded-full h-11 px-5"
                    data-testid="extra-work-submit-btn">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (
                <>
                  {mode === "admin" ? "Add Extra Work" : "Submit request"}
                  <span className="ml-2 opacity-80">·</span>
                  <span className="ml-2 tabular-nums" data-testid="extra-work-submit-total">
                    ${subtotal.toFixed(2)}
                  </span>
                </>
              )}
            </Button>
          </div>
        ) : step === "custom" ? (
          <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between gap-2 bg-gray-50">
            <Button variant="ghost" onClick={handleBack} data-testid="extra-work-custom-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button onClick={addCustomToCart}
                    disabled={!customTitle.trim() || !(parseFloat(customPrice) > 0)}
                    className="rounded-full h-11 px-5 bg-amber-600 hover:bg-amber-700 text-white font-extrabold"
                    data-testid="extra-work-custom-add-to-cart">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Add custom charge
            </Button>
          </div>
        ) : (
          <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between gap-2 bg-gray-50">
            <Button variant="ghost" onClick={handleBack} data-testid="extra-work-config-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button onClick={addToCart} disabled={!canAdd}
                    className="rounded-full h-11 px-5 bg-[#047857] hover:bg-[#065F46] text-white font-extrabold"
                    data-testid="extra-work-add-to-cart">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Add to request
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
